import { useCallback, useEffect, useRef, useState } from 'react'
import { Breadcrumb } from 'antd'
import type { ItemType } from 'antd/es/breadcrumb/Breadcrumb'
import type { ReactNode } from 'react'

import B2BecLink from '@/components/B2BLink'
import useDeviceDetect from '@/hooks/useDeviceDetect'

import styles from './B2becBreadcrumbs.module.scss'

interface BreadcrumbItem {
  title?: string
  path?: string
}

interface B2becBreadcrumbsProps {
  breadcrumbs?: BreadcrumbItem[]
}

function B2becBreadcrumbs({ breadcrumbs }: B2becBreadcrumbsProps) {
  const { isIpadLandscape } = useDeviceDetect()

  const breadcrumbsContainer = useRef<HTMLDivElement>(null)
  const [isOverflowed, setIsOverflowed] = useState(false)

  useEffect(() => {
    const container = breadcrumbsContainer.current
    const firstChild = container?.children?.[0] as HTMLElement

    if (firstChild) {
      const { offsetWidth, scrollWidth } = firstChild
      setIsOverflowed(scrollWidth > offsetWidth)
    }
  }, [])

  const filterValidItems = useCallback(
    (items: BreadcrumbItem[]): BreadcrumbItem[] =>
      items.filter((item): item is BreadcrumbItem => Boolean(item?.title)),
    [],
  )

  const findParentWithTitle = useCallback(
    (
      breadcrumbs: BreadcrumbItem[],
      startIndex: number,
    ): BreadcrumbItem | null => {
      for (let i = startIndex; i >= 0; i--) {
        if (breadcrumbs[i]?.title) {
          return breadcrumbs[i]
        }
      }
      return null
    },
    [],
  )

  const buildBreadcrumbs = useCallback(
    (originalBreadcrumbs: BreadcrumbItem[] | undefined): BreadcrumbItem[] => {
      if (!originalBreadcrumbs?.length) {
        return []
      }

      const { length } = originalBreadcrumbs
      const breadcrumbsToShow: BreadcrumbItem[] = []

      if (isIpadLandscape) {
        if (length >= 2) {
          const parent = findParentWithTitle(originalBreadcrumbs, length - 2)
          if (parent) {
            breadcrumbsToShow.push(parent)
          }
        }
        return filterValidItems(breadcrumbsToShow)
      }

      if (isOverflowed) {
        if (length > 0) {
          breadcrumbsToShow.push(originalBreadcrumbs[0]) // Home
        }

        if (length > 3) {
          breadcrumbsToShow.push(
            { title: '...' },
            originalBreadcrumbs[length - 2], // Parent Page
            originalBreadcrumbs[length - 1], // Current Page
          )
        } else if (length > 2) {
          breadcrumbsToShow.push(
            originalBreadcrumbs[length - 2], // Parent Page
            originalBreadcrumbs[length - 1], // Current Page
          )
        } else if (length > 1) {
          breadcrumbsToShow.push(originalBreadcrumbs[length - 1]) // Current Page
        }

        return filterValidItems(breadcrumbsToShow)
      }

      return filterValidItems(originalBreadcrumbs)
    },
    [isIpadLandscape, isOverflowed, filterValidItems, findParentWithTitle],
  )

  const breadcrumbsToShow = buildBreadcrumbs(breadcrumbs)

  const itemRender = useCallback(
    (route: ItemType): ReactNode => {
      const currentRoute = route as BreadcrumbItem
      if (!currentRoute?.title) {
        return null
      }

      return (
        <B2BecLink to={currentRoute.path}>
          {isIpadLandscape ? `< ${currentRoute.title}` : currentRoute.title}
        </B2BecLink>
      )
    },
    [isIpadLandscape],
  )

  return (
    <div ref={breadcrumbsContainer} className={styles['breadcrumbs-container']}>
      <Breadcrumb
        separator=">"
        items={breadcrumbsToShow as ItemType[]}
        itemRender={itemRender}
      />
    </div>
  )
}

export default B2becBreadcrumbs
